import { useTranslations } from 'next-intl';
import pushGTMEvent from '@helpers/analyticsHelpers/pushGTMEvent';
import Text from '@atoms/Text/Text';
import PickupLocationListItem, {
  type OnItemClick,
  type SuggestionType as StoreSuggestion,
} from '@molecules/PickupLocationListItem/PickupLocationListItem';
import { StyledSearchWrapper, StyledSpinner } from './StoreSearch.styles';
import { SortData } from '@occ/api-client';
import Input from '@atoms/Input/Input';
import Listbox from '@molecules/Listbox/Listbox';
import StoreSearchEmptyState from './StoreSearchEmptyState';
import useStoreSearch from '@organisms/StoreFinder/useStoreSearch';

interface Props {
  onResultClick: (res: string) => void;
  onCloseHandler: () => void;
}

const getLabel = (results: Array<SortData> | undefined) =>
  results?.length === undefined ? '0_results' : results.length > 1 ? 'multiple_results' : '1_results';

const StoreSearch = ({ onResultClick, onCloseHandler }: Props) => {
  const t = useTranslations('storeSearch');

  const { inputValue, suggestions, isLoading, onInputChange, onClear, shouldShowSuggestions } = useStoreSearch({
    onSearchComplete: (results) => {
      const label = getLabel(results as Array<SortData>);
      pushGTMEvent({
        event: 'Track',
        category: 'offers_page_store',
        action: 'store_location_searched',
        label,
        value: 0,
      });
    },
  });

  const onItemClick: OnItemClick = (suggestion) => {
    onResultClick(suggestion.storeId);
    onCloseHandler();
  };

  return (
    <>
      <StyledSearchWrapper>
        <Text type="body">{t('chooseStoreForCorrectAssortment')}</Text>
        <Input
          name="search"
          type="search"
          showSearchIcon
          aria-label={t('search')}
          placeholder={t('search')}
          onChange={onInputChange}
          value={inputValue}
          clearable
          onClear={onClear}
          autoFocus
        />
      </StyledSearchWrapper>
      {shouldShowSuggestions && (
        <Listbox items={suggestions} cards emptyState={!isLoading && <StoreSearchEmptyState searchTerm={inputValue} />}>
          {(item: StoreSuggestion) => (
            <PickupLocationListItem suggestion={item} searchTerm={inputValue} onItemClick={onItemClick} />
          )}
        </Listbox>
      )}
      {isLoading && <StyledSpinner size={32} color="black" />}
    </>
  );
};

export default StoreSearch;
